import React, { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { auth, db } from "./firebase-config";
import { doc, getDoc } from "firebase/firestore";

// Kontekst autentykacji
const AuthContext = createContext();

// Provider kontekstu autentykacji
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dla administratora - możliwość przełączania widoku
  const [viewingUserEmail, setViewingUserEmail] = useState(null);

  // Pobierz dane użytkownika z kolekcji authorizedUsers
  const fetchUserData = async (user) => {
    if (!user) return;

    try {
      const userDocRef = doc(db, "authorizedUsers", user.email);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserRole(userData.role);
        setAccessLevel(userData.accessLevel);

        // Domyślnie ustawiony użytkownik do wyświetlania to zalogowany użytkownik
        setViewingUserEmail(user.email);
      } else {
        // Jeśli użytkownik nie jest w bazie authorizedUsers
        setError("Nie masz uprawnień do korzystania z aplikacji.");
        await signOut(auth);
      }
    } catch (err) {
      console.error("Błąd podczas pobierania danych użytkownika:", err);
      setError("Wystąpił błąd podczas ładowania danych. Spróbuj ponownie.");
    }
  };

  // Logowanie przez Google
  const signInWithGoogle = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (err) {
      setError("Wystąpił błąd podczas logowania. Spróbuj ponownie.");
      console.error("Error during Google sign in:", err);
      throw err;
    }
  };

  // Wylogowanie
  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
      setUserRole(null);
      setAccessLevel(null);
      setViewingUserEmail(null);
    } catch (err) {
      setError("Wystąpił błąd podczas wylogowania.");
      console.error("Error during sign out:", err);
      throw err;
    }
  };

  // Zmiana użytkownika, którego dane są wyświetlane (tylko dla admina)
  const switchViewToUser = (email) => {
    if (userRole === "admin") {
      setViewingUserEmail(email);
    }
  };

  // Obserwowanie zmian stanu autentykacji
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        setCurrentUser(user);
        if (user) {
          await fetchUserData(user);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Auth state change error:", error);
        setError("Wystąpił błąd podczas sprawdzania stanu logowania.");
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    accessLevel,
    viewingUserEmail,
    loading,
    error,
    signInWithGoogle,
    logout,
    switchViewToUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook do używania kontekstu autentykacji
export function useAuth() {
  return useContext(AuthContext);
}
